import React, { useEffect, useState } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { SignInButton } from './SignInButton';
import { useNavigate } from 'react-router-dom';
import { useEmployee } from '../Contexts/EmployeeContext';
import { getEmployeeId } from 'src/endpoints/getEmployeeId';
import { Employee } from 'src/models';

import {
  Stack,
  Text,
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
} from '@fluentui/react';

const EMAIL_OVERRIDES: Record<string, Employee> = {
  'angel.tapiaaaav@uanl.edu.mx': {
    EmployeeId: '1905893',
    Name: 'Angel Tapia',
    Email: 'angel.tapiav@uanl.edu.mx',
    Role: 1,
    SubjectCount: 0,
    Active: true,
    Academy: 'LCC',
  },
};

export const Login: React.FC<React.PropsWithChildren<any>> = ({ children }) => {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const { setEmployee } = useEmployee();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!accounts?.length) {
      setError('No account information available.');
      return;
    }

    const [account] = accounts;
    const email = account.username.toLowerCase();

    const saveData = async () => {
      setLoading(true);
      const tokenResponse = await instance.acquireTokenSilent({
        ...loginRequest,
        account,
        forceRefresh: true,
      });
      sessionStorage.setItem('Token', tokenResponse.idToken);

      try {
        let employeeData: Employee;
        const override = EMAIL_OVERRIDES[email];
        if (override) {
          employeeData = override;
        } else {
          const response = await getEmployeeId(email);
          employeeData = response.data;
        }

        setEmployee(employeeData);

        // redirect based on role
        if (employeeData.Role === 3) {
          navigate('/dashboard', { replace: true });
        } else {
          navigate('/horario', { replace: true });
        }
      } catch (e: any) {
        console.error(e);
        setError(
          'No se encontró información del empleado. Contacte con escolar_fcfm@uanl.edu.mx o roberto.trevinocts@uanl.edu.mx'
        );
      } finally {
        setLoading(false);
      }
    };

    saveData();
  }, [isAuthenticated, accounts, navigate, setEmployee]);

  if (loading) {
    return (
      <Stack
        verticalAlign="center"
        horizontalAlign="center"
        tokens={{ childrenGap: 12 }}
        styles={{ root: { height: '70vh' } }}
      >
        <Spinner label="Cargando..." size={SpinnerSize.large} />
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack
        verticalAlign="center"
        horizontalAlign="center"
        tokens={{ padding: 20 }}
        styles={{ root: { height: '100vh' } }}
      >
        <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
      </Stack>
    );
  }

  return (
    <Stack
      verticalAlign="center"
      horizontalAlign="center"
      styles={{ root: { position: 'relative', height: '20vh' } }}
      tokens={{ childrenGap: 20 }}
    >
      {!isAuthenticated && (
        <Stack horizontalAlign="center" tokens={{ childrenGap: 8 }}>
          <Text variant="xLarge">Hub de Profesores de la FCFM</Text>
          <SignInButton />
        </Stack>
      )}
      {children}
    </Stack>
  );
};
